import React, { useState } from 'react';
import api from '../api';
import { Shield, Eye, EyeOff, FileText, CheckCircle, AlertCircle, Sparkles, Zap } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';

const PIIPreview = () => {
  const [testText, setTestText] = useState('Hello, my name is John Smith and my email is john.smith@example.com. My phone number is (555) 123-4567 and my SSN is 123-45-6789.');
  const [selectedPolicy, setSelectedPolicy] = useState('off');
  const [previewResult, setPreviewResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const policies = [
    { value: 'off', label: 'Off - No PII redaction', description: 'Analysis stored as-is with all PII' },
    { value: 'mask', label: 'Mask - Replace PII with placeholders', description: 'PII replaced with [NAME], ***-***-****, etc.' },
    { value: 'drop', label: 'Drop - Remove PII completely', description: 'PII completely removed from stored analysis' }
  ];

  const handlePreview = async () => {
    if (!testText.trim()) {
      setError('Please enter some text to preview');
      return;
    }

    setLoading(true);
    setError(null);
    setPreviewResult(null);

    try {
      const response = await api.post('/pii/preview', {
        text: testText,
        policy: selectedPolicy
      });

      setPreviewResult(response.data);
    } catch (err) {
      setError('Failed to generate preview. Please try again.');
      console.error('PII preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const sampleTexts = [
    'Hello, my name is John Smith and my email is john.smith@example.com. My phone number is (555) 123-4567.',
    'I need help with my account. My SSN is 123-45-6789 and my address is 123 Main Street, Anytown, NY 12345.',
    'Please call me back at +1-555-987-6543. My credit card number is 4532-1234-5678-9012.',
    'Dr. Sarah Johnson from ABC Medical Center called about the appointment on 12/25/2024.'
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
            <Shield className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">PII Redaction Preview</h1>
            <p className="text-muted-foreground">Preview how PII will be redacted before applying policies</p>
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 min-h-0">
        {/* Left Column - Form */}
        <div className="flex flex-col min-h-0">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col">
            {/* Policy Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Select PII Policy to Preview
              </label>
              <AnimatedDropdown
                value={selectedPolicy}
                onChange={(value) => setSelectedPolicy(value)}
                options={policies}
                placeholder="Select a policy"
                icon={Shield}
              />
              <div className="mt-3 grid grid-cols-1 gap-3">
                {policies.map((policy) => (
                  <div
                    key={policy.value}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 ${
                      selectedPolicy === policy.value
                        ? 'border-primary bg-primary/10 shadow-lg shadow-primary/10'
                        : 'border-border/60 bg-background/30 hover:bg-background/50 hover:border-primary/30'
                    }`}
                    onClick={() => setSelectedPolicy(policy.value)}
                  >
                    <div className="flex items-center mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                        selectedPolicy === policy.value
                          ? 'border-primary bg-primary'
                          : 'border-border'
                      }`}>
                        {selectedPolicy === policy.value && (
                          <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">{policy.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">{policy.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample Texts */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Quick Test Samples
              </label>
              <div className="grid grid-cols-1 gap-3">
                {sampleTexts.map((text, index) => (
                  <button
                    key={index}
                    onClick={() => setTestText(text)}
                    className="p-3 text-left bg-background/30 border border-border/60 rounded-lg hover:bg-background/50 hover:border-primary/30 transition-all duration-300 group"
                  >
                    <p className="text-sm text-foreground group-hover:text-primary transition-colors">{text}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Text Input */}
            <div className="mb-6 flex-1 flex flex-col min-h-0">
              <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Test Text
              </label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                rows={6}
                className="w-full flex-1 px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 resize-y min-h-[120px]"
                placeholder="Enter text containing PII to see how it would be redacted..."
              />
            </div>

            {/* Preview Button */}
            <button
              onClick={handlePreview}
              disabled={loading || !testText.trim()}
              className="w-full px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Generating Preview...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Generate Preview
                </>
              )}
            </button>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-4 bg-destructive/20 border border-destructive/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="flex flex-col min-h-0">
          {previewResult ? (
            <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col overflow-y-auto">
              <div className="flex items-center gap-2 mb-6 sticky top-0 bg-card/95 backdrop-blur-md pb-4 border-b border-border/50">
                <CheckCircle className="w-5 h-5 text-success" />
                <h4 className="text-lg font-semibold text-foreground">Preview Results</h4>
              </div>
              
              <div className="space-y-4 flex-1">
                {/* Original Text */}
                <div className="bg-background/30 border border-border/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">Original Text</h5>
                  </div>
                  <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">{previewResult.original_text}</p>
                </div>

                {/* Redacted Text */}
                <div className="bg-background/30 border border-border/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <EyeOff className="w-4 h-4 text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">Redacted Text</h5>
                  </div>
                  <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">{previewResult.redacted_text}</p>
                </div>

                {/* PII Detection Results */}
                {previewResult.pii_detected && Object.keys(previewResult.pii_detected).length > 0 && (
                  <div className="bg-background/30 border border-border/60 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-warning" />
                      <h5 className="text-sm font-semibold text-foreground">Detected PII</h5>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(previewResult.pii_detected).map(([type, matches]) => (
                        <div key={type} className="flex items-center gap-2 px-3 py-2 bg-warning/20 border border-warning/30 rounded-lg">
                          <span className="text-xs font-semibold text-warning uppercase">
                            {type}
                          </span>
                          <span className="text-sm text-foreground font-medium">
                            {matches.length} instance{matches.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Redaction Details */}
                {previewResult.redactions && previewResult.redactions.length > 0 && (
                  <div className="bg-background/30 border border-border/60 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4 text-primary" />
                      <h5 className="text-sm font-semibold text-foreground">Redaction Details</h5>
                    </div>
                    <div className="space-y-2">
                      {previewResult.redactions.map((redaction, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border/40">
                          <span className="px-2 py-1 bg-primary/20 text-primary border border-primary/30 rounded text-xs font-medium">
                            {redaction.type}
                          </span>
                          <span className="text-sm text-muted-foreground flex-1 truncate">"{redaction.original}"</span>
                          <span className="text-primary">→</span>
                          <span className="text-sm text-foreground font-medium flex-1 truncate">"{redaction.masked}"</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Policy Summary */}
                <div className="bg-background/30 border border-border/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4 text-success" />
                    <h5 className="text-sm font-semibold text-foreground">Policy Summary</h5>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Policy:</span>
                      <span className="px-2 py-1 bg-primary/20 text-primary border border-primary/30 rounded text-xs font-medium">
                        {previewResult.policy}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Redaction Applied:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${
                        previewResult.redaction_applied 
                          ? 'bg-success/20 text-success border-success/30' 
                          : 'bg-muted text-muted-foreground border-border/40'
                      }`}>
                        {previewResult.redaction_applied ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex items-center justify-center">
              <div className="text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Generate a preview to see results here</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PIIPreview;
